import {
    createStyles,
    Dialog,
    Grid,
    useTheme,
    useMediaQuery,
    makeStyles,
    Theme,
    Typography
} from "@material-ui/core"
import React, { memo, useCallback } from "react"
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal";
import { Localized } from '@fluent/react';
import Screen from 'shared/Components/Skeleton/Screen'
import {
    IBasketDeliveryFormValues,
    IMeetingPlanningOrderConfirmationModal,
    IMeetingPlanningOrderModalAction
} from "../../shared/Types/MeetingPlanning/deliveryForm"
import { StandardButton } from "../../shared/Components/Button/StandardButton"
import { Alert, AlertTitle } from "@material-ui/lab"

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: theme.palette.common.white,
        },
        wrap: {
            padding: '65px 0',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '580px'
        },
        mr_2: {
            marginRight: theme.spacing(2),
        },
        mb_1: {
            marginBottom: theme.spacing(1),
        },
        mb_2: {
            marginBottom: theme.spacing(2),
        },
        mb_3: {
            marginBottom: theme.spacing(3),
        },
        bookingTitle: {
            fontSize: '24px',
            fontWeight: 700
        },
        textWithBar: {
            position: 'relative',
            paddingLeft: theme.spacing(2),
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                borderRadius: 2,
                backgroundColor: theme.palette.error.main,
            },
        },
        alertWrap: {
            color: "#0369A1",
            background: "#F0F9FF",
            borderRadius: ".5rem",
            marginBottom: theme.spacing(2),

            "& .MuiAlert-icon": {
                color: "#0369A1",
            },
        },
    }),
);

interface IModalProps {
    meetingPlanningConfirmModalData: IMeetingPlanningOrderConfirmationModal
    onClose: (actionType: IMeetingPlanningOrderModalAction) => void
}

interface IModalContentProps {
    classes: ReturnType<typeof useStyles>
    btnMargin: string
    btnContainerWrap: 'nowrap' | 'wrap'
    formData: IBasketDeliveryFormValues | null
    onAccept: () => void
    onDenied: () => void
    onClose: () => void
}

const ModalContent = ({
  classes,
  btnMargin,
  btnContainerWrap,
  formData,
  onAccept,
  onDenied,
  onClose
}: IModalContentProps) => <Screen
    name="statusView"
    fitPage
    fullHeight
    showPrimaryTopBar
    onBackButtonPress={onClose}
    backButtonIcon="cross"
>
    <Grid container className={classes.wrap} direction="column" wrap="nowrap">
        <Grid container item alignItems="center" justifyContent="center" className={classes.mb_2}>
            <Localized id={"confirm-meeting-planning-modal-title"}>
                <Typography variant="h4" color="error" className={classes.bookingTitle}>Fristen er overskredet</Typography>
            </Localized>
        </Grid>
        <Grid container item alignItems="center" justifyContent="center" className={classes.mb_2}>
            <Alert severity="info" className={classes.alertWrap}>
                <AlertTitle>
                    <Typography variant="caption">{formData?.deadlineMessage}</Typography>
                </AlertTitle>
            </Alert>
        </Grid>
        <Grid container item alignItems="center" justifyContent="center" className={classes.mb_3}>
            <Localized id="confirm-meeting-planning-modal-description">
                <Typography variant="h6" className={classes.textWithBar}>Du kan stadig afgive bestillingen, men den skal godkendes af personalet, hvilket betyder, at du ikke kan forvente, at den bliver leveret.</Typography>
            </Localized>
        </Grid>
        <Grid container item alignItems="center" justifyContent="center" className={classes.mb_2}>
            <Typography variant="body1">{formData?.deadlineMerchantMessage}</Typography>
        </Grid>
        <Grid container item wrap={btnContainerWrap} alignItems="center" justifyContent="center">
            <StandardButton
                className={btnMargin}
                variant="outlined"
                color="black"
                size="medium"
                onClick={onDenied}
            >
                <Localized id="confirm-meeting-planning-modal-button-denied">
                    Gennemfør ikke bestillingen
                </Localized>
            </StandardButton>
            <StandardButton
                variant="contained"
                size="medium"
                color="black"
                onClick={onAccept}
            >
                <Localized id="confirm-meeting-planning-modal-button-accept">
                    Gennemfør bestilling
                </Localized>
            </StandardButton>
        </Grid>
    </Grid>
</Screen>

const MeetingPlanningCompleteOrderConfirmModal = ({ meetingPlanningConfirmModalData, onClose }: IModalProps) => {
    const classes = useStyles()
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
    const btnMargin = isSmUp ? classes.mr_2 : classes.mb_2;
    const btnContainerWrap: 'wrap' | 'nowrap' = isSmUp ? 'nowrap' : 'wrap';

    const handleAccept = useCallback(() => onClose(IMeetingPlanningOrderModalAction.Accept), [onClose])

    const handleDenied = useCallback(() => onClose(IMeetingPlanningOrderModalAction.Denied), [onClose])

    const handleClose = useCallback(() => onClose(IMeetingPlanningOrderModalAction.Close), [onClose])

    return <div>
        <Dialog fullScreen open={meetingPlanningConfirmModalData.isOpen}>
            <ErrorHandlerModal close={handleClose}>
                <ModalContent
                    classes={classes}
                    btnMargin={btnMargin}
                    btnContainerWrap={btnContainerWrap}
                    formData={meetingPlanningConfirmModalData.formData}
                    onAccept={handleAccept}
                    onDenied={handleDenied}
                    onClose={handleClose}
                />
            </ErrorHandlerModal>
        </Dialog>
    </div>
}

export default memo(MeetingPlanningCompleteOrderConfirmModal);
